/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import cx from 'classnames';
import type { VoidFunctionComponent } from 'react';

import { VENDORED_DEPENDENCY_DETECTION } from '../../../../../shared/featureFlags/flagNames';
import { useIsFeatureEnabled } from '../../../../helpers/features';
import { IconAndNode, icons } from '../../../generic';
import TooltipIconBadge from '../../../generic/Badge/TooltipIconBadge';
import Checkbox from '../../../generic/Forms/Checkbox';
import PackageLabelsList from '../../../shared/packageLabels/PackageLabelsList';
import useSnippetGrouping from '../hooks/useSnippetGrouping';
import useSnippetsForPackage from '../hooks/useSnippetsForPackage';
import type { Snippet } from '../../../../../shared/snippets';
import type { SnippetSelectionState } from '../hooks/useSnippetSelectionState';

import IssueCounts from './IssueCounts';

import styles from './index.module.less';

const SnippetConversionIconBadge: VoidFunctionComponent = () => (
  <span className="margin-left-xs">
    <TooltipIconBadge
      id="snipet-conversion-icon-badge"
      tooltip="Manually Converted"
      placement="top"
      showTooltip
      color="light-blue"
      icon={icons.arrowRightFromArc}
    />
  </span>
);

const VendoredIconBadge: VoidFunctionComponent = () => (
  <span className="margin-left-xs">
    <TooltipIconBadge
      id="vendored-icon-badge"
      tooltip="Vendored Match"
      placement="top"
      showTooltip
      color="light-gray"
      icon={icons.boxesPacking} />
  </span>
);

const snippetKindIcons = {
  snippet: icons.receipt,
  file: icons.fileOutline,
};

export type Props = {
  snippet: Snippet;
  selectionState: SnippetSelectionState,
  shouldShowBorderTop: boolean;
  shouldShowBorderBottom: boolean;
  onSelectSnippet: (snippet: Snippet) => void;
};

/**
 * Displays snippet row for the snippets table.
 */
const SnippetRow: VoidFunctionComponent<Props> = ({
  snippet,
  selectionState,
  shouldShowBorderTop,
  shouldShowBorderBottom,
  onSelectSnippet,
}) => {
  const { snippetGrouping } = useSnippetGrouping();
  const isGroupingByPackage = snippetGrouping === 'package';

  // Use the snippet that have been loaded for a given package to handle "all to some" selection state.
  const { data: packageSnippetData } = useSnippetsForPackage(snippet.packageId, isGroupingByPackage);
  const packageSnippets = packageSnippetData?.pages.flatMap((page) => page.results) ?? [];
  const isSnippetConversionSupported = useIsFeatureEnabled(VENDORED_DEPENDENCY_DETECTION);
  const shouldShowConvertedIcon = snippet.isConverted && isSnippetConversionSupported;
  const shouldShowVendoredIcon = snippet.isVendored && isSnippetConversionSupported && !shouldShowConvertedIcon;

  return (
    <div
      key={snippet.id}
      className={cx(styles.tableBodyRow, {
        [styles.tableBodyRowPackages]: isGroupingByPackage,
        [styles.tableBodyRowRejected]: !!snippet.rejectionDetails,
        [styles.tableBodyRowBorderTop]: shouldShowBorderTop,
        [styles.tableBodyRowBorderBottom]: shouldShowBorderBottom,
      })}
      // Stop mousedown from reaching the document-level listener in useOnClickOutside,
      // which would close the drawer before the click event opens it (race condition).
      onMouseDown={(e) => e.stopPropagation()}
      onClick={() => onSelectSnippet(snippet)}
    >
      {isGroupingByPackage && (
        // Empty span to align with expand/collapse checkbox in package rows
        <span />
      )}
      <span>
        <Checkbox
          isChecked={selectionState.isSnippetChecked(snippet.id, snippet.packageId)}
          onChange={() => selectionState.toggleSnippet(snippet.id, snippet.packageId, packageSnippets)}
          shouldStopPropagation
          isRippleDisabled
        />
      </span>
      <span>
        <IconAndNode icon={snippetKindIcons[snippet.kind]} marginAmount="0.5em">
          <span className={cx({ [styles.packageRejected]: !!snippet.rejectionDetails })}>{snippet.package}</span>
        </IconAndNode>
        {shouldShowConvertedIcon &&
        <SnippetConversionIconBadge />}
        {shouldShowVendoredIcon &&
        <VendoredIconBadge />}
      </span>
      <span>{snippet.version}</span>
      <span>
        <IssueCounts id={snippet.id} isRejected={!!snippet.rejectionDetails} issueCounts={snippet.issueCounts} />
      </span>
      <span>{snippet.matchCount}</span>
      <span>
        <PackageLabelsList
          labels={snippet.labels}
          dependencyVersion={snippet.version}
          labelPrefix=""
          isLeftAligned
        />
      </span>
    </div>
  );
};

export default SnippetRow;
